<template>
  <ModalBase :show-modal="showModal" width-md auto-h @close-modal="close">
    <template v-slot:titlebar>
      <TitleBar variant-class="title-modal">
        <template v-slot:title>
          <span v-translate>Edit trip odometer</span>
        </template>
      </TitleBar>
    </template>

    <template v-slot:maincontent>
      <div v-if="trip" class="trip-odometer-modal">
        <div class="trip-odometer-modal__left">
          <div class="trip-odometer-modal__summary">
            <div class="trip-odometer-modal__row">
              <strong>{{ $gettext('Trip') }}:</strong>
              <span>#{{ trip.number || trip.id }}</span>
            </div>
            <div class="trip-odometer-modal__row">
              <strong>{{ $gettext('Date') }}:</strong>
              <span>{{ tripDate }}</span>
            </div>
            <div class="trip-odometer-modal__row">
              <strong>{{ $gettext('Distance') }}:</strong>
              <span>{{ trip.total_distance }} km</span>
            </div>
            <div class="trip-odometer-modal__row">
              <strong>{{ $gettext('Route') }}:</strong>
              <span>{{ routeText }}</span>
            </div>
          </div>

          <b-form-group class="mb-0 mt-3" :label="$gettext('Odometer')">
            <b-input-group>
              <template #append>
                <b-input-group-text>km</b-input-group-text>
              </template>
              <b-form-input
                v-model.number="endOdometer"
                type="number"
                step="0.1"
                min="0"
                class="trip-odometer-modal__odometer-input"
                @wheel.native="$event.target.blur()"
              />
            </b-input-group>
            <small v-if="endOdometer !== null && !(Number(endOdometer) > 0)" class="text-danger d-block mt-1">
              {{ $gettext('Please enter a value greater than 0.') }}
            </small>
          </b-form-group>
        </div>

        <div class="trip-odometer-modal__map">
          <GoogleMap v-if="isMapAvailable" ref="googleMap" />
          <div v-else class="trip-odometer-modal__map-unavailable">
            <span v-translate class="disabled-map-message">No route available</span>
            <img src="/static/img/trips/norouteavailable.png" alt="No route available" class="disabled-map-img">
          </div>
        </div>
      </div>
    </template>

    <template v-slot:actions>
      <div class="trip-odometer-modal__actions">
        <GekkoButton
          @click="close"
          :text="$gettext('Cancel')"
          variant="link"
          class="text-primary px-0 mr-auto"
        />
        <GekkoButton
          @click="save"
          :text="$gettext('Save')"
          icon="fa-solid fa-floppy-disk"
          class="ml-auto position-relative"
          :loading="saving"
        />
      </div>
    </template>
  </ModalBase>
</template>

<script>
import moment from 'moment'

import ModalBase from '@/components/base_components/ModalBase'
import TitleBar from '@/components/TitleBar'
import GekkoButton from '@/components/GekkoButton'
import GoogleMap from '@/components/trips/GoogleMap'
import TripService from '@/store/services/trip.service'
import { mapGetters } from 'vuex'

export default {
  name: 'TripOdometerModal',
  components: { ModalBase, TitleBar, GekkoButton, GoogleMap },
  props: {
    showModal: {
      type: Boolean,
      default: false,
    },
    trip: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      endOdometer: null,
      saving: false,
    }
  },
  computed: {
    ...mapGetters({
      isPremium: 'getIsPremium',
      mapPolyline: 'tripStore/getMapPolyline',
    }),
    tripDate() {
      if (!this.trip || !this.trip.date) {
        return '-'
      }
      return moment(this.trip.date).format('YYYY-MM-DD')
    },
    routeText() {
      if (!this.trip) {
        return '-'
      }

      const startName = this.trip.start && this.trip.start.name ? this.trip.start.name : '-'
      const endName = this.trip.end && this.trip.end.name ? this.trip.end.name : '-'
      return `${startName} -> ${endName}`
    },
    isMapAvailable() {
      return this.isPremium || !!(this.trip && this.trip.route)
    },
  },
  watch: {
    showModal(newVal) {
      if (newVal && this.trip) this.onOpen()
    },
    trip(newVal) {
      if (newVal && this.showModal) this.onOpen()
    },
  },
  methods: {
    onOpen() {
      this.endOdometer = Number(this.trip.end_odometer)
      this.loadMapPolyline()
    },
    async loadMapPolyline() {
      if (!this.trip || !this.trip.id || !this.trip.route) {
        this.$store.commit('tripStore/setMapPolyline', [])
        return
      }

      try {
        const response = await TripService.getRoute(this.trip.id)
        if (response.status !== 204) {
          const mapPolyline = TripService.calculatePolyline(response.data)
          this.$store.commit('tripStore/setMapPolyline', mapPolyline)
          return
        }
      } catch (error) {
        // Keep fallback UI visible if route cannot be loaded.
      }

      this.$store.commit('tripStore/setMapPolyline', [])
    },
    close() {
      this.$store.commit('tripStore/setMapPolyline', [])
      this.$emit('close')
    },
    async save() {
      if (!this.trip || !this.trip.id || !(Number(this.endOdometer) > 0)) {
        return
      }

      this.saving = true

      try {
        const tripResponse = await TripService.retrieve(this.trip.id)
        const payload = { ...tripResponse.data, end_odometer: Number(this.endOdometer) }
        const response = await TripService.update(this.trip.id, payload)

        this.$store.commit('setAlert', {
          title: this.$gettext('Trip updated'),
          text: this.$gettext('The odometer was updated successfully.'),
          duration: 4,
        })

        this.$emit('saved', response.data)
      } catch (error) {
        this.$store.commit('setAlert', {
          title: this.$gettext('Error editing trip'),
          text: this.$gettext('The odometer could not be updated.'),
          variant: 'danger',
          duration: 4,
        })
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped lang="scss">
.trip-odometer-modal {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.6fr);
  gap: 1.25rem;
  min-height: 360px;

  &__odometer-input {
    /* Hide native number input spin buttons */
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
    -moz-appearance: textfield;
    appearance: textfield;
  }

  &__left {
    display: flex;
    flex-direction: column;
  }

  &__summary {
    border: 1px solid #eef1f4;
    border-radius: 8px;
    padding: 0.75rem;
    background: #fafcfe;
  }

  &__row {
    display: flex;
    gap: 0.5rem;
    font-size: 0.9rem;
    line-height: 1.4;

    &:not(:last-child) {
      margin-bottom: 0.3rem;
    }
  }

  &__actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
  }

  &__map {
    position: relative;
    border-radius: 8px;
    overflow: hidden;
    min-height: 320px;
  }

  &__map-unavailable {
    position: relative;
    height: 100%;
  }
}

.disabled-map-message{
  font-size: 25px;
  color: #ff5408;
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
}

.disabled-map-img {
  height: 100%;
  width: 100%;
  object-fit: contain;
}
</style>
